import { SolutionStory } from '../../services/api';
import { JOURNEY_ORDER, STAGE_ORDER, StoryStage } from './storyUiTokens';

/** Collect impact points (impact_1 … impact_4), truncate to fit within maxChars */
export function buildImpactSummary(story: SolutionStory, maxChars = 140): string {
  const points = [story.impact_1, story.impact_2, story.impact_3, story.impact_4]
    .filter(Boolean)
    .map((s) => s!.trim());

  if (points.length === 0) return '';

  let combined = '';
  for (let i = 0; i < points.length; i++) {
    const separator = i === 0 ? '' : ' · ';
    const candidate = combined + separator + points[i];
    if (candidate.length > maxChars) {
      if (i === 0) {
        return points[0].substring(0, maxChars - 1) + '…';
      }
      return combined + '…';
    }
    combined = candidate;
  }
  return combined;
}

export function collectPoints(points: (string | null | undefined)[]): string[] {
  return points
    .filter(Boolean)
    .map((s) => s!.trim())
    .filter(Boolean);
}

export function joinPoints(points: (string | null | undefined)[]): string {
  return collectPoints(points).join(' ');
}

export function splitCsv(value?: string | null): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Map opportunity/project status to presentation journey stage.
 * project_status = Closed → delivered; opportunity Closed Won → won; etc.
 */
export function resolveStoryStage(story: SolutionStory): StoryStage {
  const opp = (story.opportunity_status || '').toLowerCase().trim();
  const proj = (story.project_status || '').toLowerCase().replace(/-/g, ' ').trim();

  if (opp.includes('renew')) return 'renewed';
  // Project is completed (delivered) once it is Closed or Signed Off.
  if (
    proj === 'closed' ||
    proj.includes('sign off') ||
    proj === 'signoff' ||
    opp.includes('deliver')
  ) {
    return 'delivered';
  }
  // Opportunity "Signed" → Signed / In Delivery (2nd journey step)
  if (opp.includes('sign')) return 'signed';
  if (opp.includes('won') || opp.includes('closed won')) return 'won';

  // Fallback: raw status token match
  for (const stage of STAGE_ORDER) {
    if (opp.includes(stage) || proj.includes(stage)) return stage;
  }
  return 'won';
}

/** Presentation tabs in the detail modal (Solution Story / Deal Win Story / Technical Case Study). */
export type StoryTab = 'won' | 'delivery' | 'completed';

export const STORY_TAB_ORDER: StoryTab[] = ['completed', 'won', 'delivery'];

export const STORY_TAB_LABEL: Record<StoryTab, string> = {
  completed: 'Solution Story',
  won: 'Deal Win Story',
  delivery: 'Technical Case Study',
};

/** Map the story's resolved stage to the presentation tab it should open on. */
export function resolveStoryTab(story: SolutionStory): StoryTab {
  const stage = resolveStoryStage(story);
  if (stage === 'won') return 'won';
  if (stage === 'signed') return 'delivery';
  // delivered / renewed → the fullest, final view
  return 'completed';
}

/** Journey checkmarks for modal timeline (Won → Signed → Delivered only). */
export function resolveJourneyDone(story: SolutionStory): boolean[] {
  let reached = resolveStoryStage(story);
  // Renewed is past delivery — treat as fully delivered on the journey bar
  if (reached === 'renewed') reached = 'delivered';
  const idx = JOURNEY_ORDER.indexOf(reached as (typeof JOURNEY_ORDER)[number]);
  const safeIdx = idx < 0 ? 0 : idx;
  return JOURNEY_ORDER.map((_, i) => i <= safeIdx);
}

/** "Updated <date>" from the story's real updated_time, or a placeholder when never edited. */
export function formatUpdatedTime(updatedTime?: string | null): string {
  if (!updatedTime) return 'Not yet edited';
  const d = new Date(updatedTime);
  if (Number.isNaN(d.getTime())) return 'Not yet edited';
  return `Updated ${d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}`;
}

export function clientShortName(accountName?: string | null): string {
  if (!accountName) return 'Client';
  return accountName.split(/\s+/)[0];
}

export function buildTagline(story: SolutionStory): string {
  return [story.region, story.industry, story.practice].filter(Boolean).join(' · ');
}

/** short (upper) → preferred long-form name from industry_labels */
export function buildIndustryShortToCanonical(
  labels: Record<string, string> | undefined
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const [long, short] of Object.entries(labels || {})) {
    const s = (short || '').trim();
    if (!s) continue;
    const key = s.toUpperCase();
    // Prefer the longer / more specific canonical name when shorts collide (e.g. RCE)
    if (!map[key] || long.length > map[key].length) {
      map[key] = long;
    }
  }
  return map;
}

/**
 * Expand an industry value to its canonical long form using mappings.json industry_labels.
 * "FSI" → "Financial Services & Insurance"; unknown values pass through.
 */
export function expandIndustryToCanonical(
  raw: string,
  labels: Record<string, string> | undefined
): string {
  const trimmed = (raw || '').trim();
  if (!trimmed || !labels) return trimmed;

  const shortToLong = buildIndustryShortToCanonical(labels);
  const byShort = shortToLong[trimmed.toUpperCase()];
  if (byShort) return byShort;

  // Exact / case-insensitive long-form match → normalize to the mapped key
  for (const long of Object.keys(labels)) {
    if (long === trimmed) {
      const short = labels[long];
      return shortToLong[(short || '').toUpperCase()] || long;
    }
  }
  const lower = trimmed.toLowerCase();
  for (const long of Object.keys(labels)) {
    if (long.toLowerCase() === lower) {
      const short = labels[long];
      return shortToLong[(short || '').toUpperCase()] || long;
    }
  }

  return trimmed;
}

/** Collapse short + long variants into unique canonical industry options for the filter UI. */
export function dedupeIndustryFilterOptions(
  options: string[],
  labels: Record<string, string> | undefined
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const opt of options) {
    if (!opt?.trim()) continue;
    const expanded = expandIndustryToCanonical(opt, labels);
    const short = labels?.[expanded];
    const dedupeKey = short
      ? `short:${short.toUpperCase()}`
      : `long:${expanded.toLowerCase()}`;
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    out.push(expanded);
  }
  return out.sort((a, b) => a.localeCompare(b));
}

/**
 * Expand selected filter values so the API matches both short and long DB values
 * (e.g. selecting "Financial Services & Insurance" also matches rows stored as "FSI").
 */
export function industryFilterValuesForApi(
  selected: string[],
  labels: Record<string, string> | undefined
): string[] {
  if (!selected.length) return [];
  const out = new Set<string>();
  for (const value of selected) {
    const trimmed = (value || '').trim();
    if (!trimmed) continue;
    out.add(trimmed);
    const canonical = expandIndustryToCanonical(trimmed, labels);
    out.add(canonical);
    const short = labels?.[canonical];
    if (short) {
      out.add(short);
      for (const [long, sh] of Object.entries(labels || {})) {
        if ((sh || '').toUpperCase() === short.toUpperCase()) out.add(long);
      }
    }
  }
  return Array.from(out);
}
